import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Link } from 'react-router-dom'
import { truncate, isEmpty } from 'lodash'
import { IconCheck, IconX, IconShieldLock } from '@tabler/icons-react'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { closeModal, openModal } from '@/reducers/ui/ui.redux'
import {
  createOrganization,
  fetchSecuritySettingsAccess,
} from '@/reducers/organizations/organizations.actions'
import { Pill } from '@/components/pills/Pill'
import { getSearchSchemaForBoolean } from '@/utils/helpers'
import { PageHeader } from '@/components/headers/PageHeader'
import {
  OrganizationSecuritySettingsModal,
  SECURITY_SETTINGS_MODAL_ID,
} from './OrganizationSecuritySettingsModal'

const OrganizationsPage = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const organizations = useSelector((state) => state.organizations)
  const [canManageSecuritySettings, setCanManageSecuritySettings] = useState(false)
  const [securitySettingsOrg, setSecuritySettingsOrg] = useState(null)

  useEffect(() => {
    dispatch(fetchSecuritySettingsAccess(setCanManageSecuritySettings))
  }, [dispatch])

  const openSecuritySettings = (event, row) => {
    setSecuritySettingsOrg(row)
    dispatch(openModal(SECURITY_SETTINGS_MODAL_ID))
  }

  const handleCreateOrganization = (formValues) => {
    dispatch(
      createOrganization({
        name: formValues.name,
        push: history.push,
        ringcentral_customer: formValues.ringcentral_customer,
      })
    )
    dispatch(closeModal())
  }

  const showEnabled = (enabled) =>
    enabled ? (
      <Pill circular success outlined xs icon={<IconCheck />} />
    ) : (
      <Pill circular critical outlined xs icon={<IconX />} />
    )

  const columns = [
    {
      accessor: 'name',
      label: 'Organization Name',
      format: (value, row) => (
        <Link to={`/organizations/${row.id}`} className="table-link">
          {truncate(value, { length: 40 })}
        </Link>
      ),
      sticky: true,
      collapsing: true,
      isSearchable: true,
    },
    {
      accessor: 'id',
      label: 'ID',
      collapsing: true,
      isSearchable: true,
    },
    {
      accessor: 'parent_id',
      label: 'Hierarchy Parent',
      collapsing: true,
      isSearchable: true,
    },
    {
      accessor: 'production',
      label: 'Production',
      collapsing: true,
      format: (value) => showEnabled(value),
      isSearchable: true,
      ...getSearchSchemaForBoolean('Production', 'Production', 'Not Production'),
    },
    {
      accessor: 'desktop_enterprise_enabled',
      label: 'Enterprise',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Desktop Enterprise'),
    },
    {
      accessor: 'ringcentral_customer',
      label: 'RingCentral Customer',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean(
        'RingCentral Customer',
        'RingCentral Customer',
        'Not RingCentral Customer'
      ),
    },
    {
      accessor: 'unredacted_enabled',
      label: 'Unredacted',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Unredacted', 'Unredacted', 'Redacted'),
    },
    {
      accessor: 'requires_compliance',
      label: 'Requires Compliance',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Requires Compliance', 'Required', 'Not Required'),
    },
    {
      // SSN redaction is only in effect when the org both requires compliance
      // and has the redact_ssn flag set, so display the combination of both.
      accessor: 'redact_ssn',
      label: 'SSN Redaction',
      format: (_value, row) => showEnabled(row.requires_compliance && row.redact_ssn),
      collapsing: true,
    },
    {
      accessor: 'save_audio',
      label: 'Save Audio',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Save Audio'),
    },
    {
      accessor: 'screen_capture_enabled',
      label: 'Screen Capture',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Screen Capture'),
    },

    {
      accessor: 'qa_enabled',
      label: 'Real-Time QA',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Real-Time QA'),
    },
    {
      accessor: 'qa_copilot_enabled',
      label: 'Quality',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Quality'),
    },
    {
      accessor: 'qa_disputes_enabled',
      label: 'Disputes',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('QA Disputes'),
    },
    {
      accessor: 'realtime_coaching_access',
      label: 'Real-Time Coaching',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Real-Time Coaching'),
    },
    {
      accessor: 'call_summarization_enabled',
      label: 'Call Summary',
      format: (value) => showEnabled(value),
      collapsing: true,
      isSearchable: true,
      ...getSearchSchemaForBoolean('Call Summary'),
    },
    {
      accessor: 'prompt_allowance',
      label: 'Prompt Allowance',
      collapsing: true,
      isSearchable: true,
    },
  ]

  // Only allowlisted users get the security-settings action (core-service
  // also enforces this on write).
  const actions = canManageSecuritySettings
    ? [{ label: 'Security Settings', fn: openSecuritySettings, icon: <IconShieldLock /> }]
    : []

  return (
    <>
      <PageHeader
        isDataGrid
        title="Organizations"
        data-testid="organizations-page"
        actions={
          <ButtonAndFormModal
            buttonLabel="Create Organization"
            modalTitle="Create Organization"
            modalId="organizations/create"
            modalProps={{ size: 'tiny' }}
            buttonProps={{ primary: true }}
            form={
              <AbstractForm
                schema={[
                  { name: 'name', type: 'text', label: 'Name', required: true },
                  { name: 'ringcentral_customer', type: 'checkbox', label: 'RingCentral Customer' },
                ]}
                onSubmit={handleCreateOrganization}
                buttonLabel="Create"
                onClose={() => dispatch(closeModal())}
              />
            }
          />
        }
      />

      <AdvancedTable
        loading={isEmpty(organizations)}
        dataGrid
        defaultOrderBy="name"
        columns={columns}
        rows={organizations}
        actions={actions}
        rowsPerPage={30}
        stickyAction
        pagination
      />

      {canManageSecuritySettings && (
        <OrganizationSecuritySettingsModal organization={securitySettingsOrg} />
      )}
    </>
  )
}

export default OrganizationsPage
